import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ProductAnalytics {
  productId: string;
  productName: string;
  category: string | null;
  totalQuantitySold: number;
  totalRevenue: number;
  avgUnitPrice: number;
  dealCount: number;
}

export interface ProductPerformance {
  topSellers: ProductAnalytics[];
  revenueByCategory: { category: string; revenue: number; count: number }[];
  pricingPerformance: {
    productId: string;
    productName: string;
    basePrice: number;
    avgSoldPrice: number;
    priceVariance: number;
    discountRate: number;
  }[];
  summary: {
    totalProducts: number;
    activeProducts: number;
    totalRevenue: number;
    avgOrderValue: number;
    topCategory: string;
  };
}

export function useProductAnalytics() {
  return useQuery({
    queryKey: ['product-analytics'],
    queryFn: async () => {
      // Fetch deal line items with product info
      const { data: lineItems, error: lineError } = await supabase
        .from('deal_line_items')
        .select(`
          *,
          product:products(*),
          deal:deals(status)
        `);

      if (lineError) throw lineError;

      // Fetch all products for comparison
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('*');

      if (productsError) throw productsError;

      // Filter to only won deals
      const wonLineItems = lineItems?.filter(item => item.deal?.status === 'won') || [];

      // Calculate product analytics
      const productMap = new Map<string, ProductAnalytics>();

      wonLineItems.forEach((item) => {
        const productId = item.product_id || 'unknown';
        const existing = productMap.get(productId);

        if (existing) {
          existing.totalQuantitySold += item.quantity;
          existing.totalRevenue += item.total_price;
          existing.dealCount += 1;
        } else {
          productMap.set(productId, {
            productId,
            productName: item.product?.name || item.name,
            category: item.product?.category || null,
            totalQuantitySold: item.quantity,
            totalRevenue: item.total_price,
            avgUnitPrice: item.unit_price,
            dealCount: 1,
          });
        }
      });

      // Calculate averages
      productMap.forEach((analytics) => {
        analytics.avgUnitPrice = analytics.totalRevenue / analytics.totalQuantitySold;
      });

      const productAnalytics = Array.from(productMap.values());

      // Top sellers by revenue
      const topSellers = [...productAnalytics]
        .sort((a, b) => b.totalRevenue - a.totalRevenue)
        .slice(0, 10);

      // Revenue by category
      const categoryMap = new Map<string, { revenue: number; count: number }>();
      productAnalytics.forEach((p) => {
        const cat = p.category || 'Uncategorized';
        const existing = categoryMap.get(cat);
        if (existing) {
          existing.revenue += p.totalRevenue;
          existing.count += p.dealCount;
        } else {
          categoryMap.set(cat, { revenue: p.totalRevenue, count: p.dealCount });
        }
      });

      const revenueByCategory = Array.from(categoryMap.entries())
        .map(([category, data]) => ({ category, ...data }))
        .sort((a, b) => b.revenue - a.revenue);

      // Pricing performance
      const pricingPerformance = products
        ?.map((product) => {
          const analytics = productMap.get(product.id);
          const avgSoldPrice = analytics?.avgUnitPrice || product.base_price;
          const priceVariance = ((avgSoldPrice - product.base_price) / product.base_price) * 100;
          const discountRate = priceVariance < 0 ? Math.abs(priceVariance) : 0;

          return {
            productId: product.id,
            productName: product.name,
            basePrice: product.base_price,
            avgSoldPrice,
            priceVariance,
            discountRate,
          };
        })
        .filter((p) => productMap.has(p.productId))
        .sort((a, b) => b.priceVariance - a.priceVariance) || [];

      // Summary
      const totalRevenue = productAnalytics.reduce((sum, p) => sum + p.totalRevenue, 0);
      const totalDeals = productAnalytics.reduce((sum, p) => sum + p.dealCount, 0);

      const summary = {
        totalProducts: products?.length || 0,
        activeProducts: products?.filter((p) => p.is_active).length || 0,
        totalRevenue,
        avgOrderValue: totalDeals > 0 ? totalRevenue / totalDeals : 0,
        topCategory: revenueByCategory[0]?.category || 'N/A',
      };

      return {
        topSellers,
        revenueByCategory,
        pricingPerformance,
        summary,
      } as ProductPerformance;
    },
  });
}
